/**
 * Copyright 2018 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import i18n from "i18next";

import {
  createAction,
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
  startLoading,
  stopLoading
} from "openstack-uicore-foundation/lib/utils/actions";
import { getAccessTokenSafely } from "../utils/methods";
import { ERROR_CODE_404, ERROR_CODE_412 } from "../utils/constants";
import {
  setSnackbarMessage,
  snackbarErrorHandler,
  snackbarSuccessHandler
} from "./base-actions";

export const REQUEST_CURRENT_CART = "REQUEST_CURRENT_CART";
export const RECEIVE_CURRENT_CART = "RECEIVE_CURRENT_CART";
export const FORM_ADDED_TO_CART = "FORM_ADDED_TO_CART";
export const CART_FORM_UPDATED = "CART_FORM_UPDATED";
export const REQUEST_CART_FORM = "REQUEST_CART_FORM";
export const RECEIVE_CART_FORM = "RECEIVE_CART_FORM";
export const CART_FORM_DELETED = "CART_FORM_DELETED";
export const CART_STATUS_UPDATED = "CART_STATUS_UPDATED";
export const RECEIVE_PAYMENT_PROFILE = "RECEIVE_PAYMENT_PROFILE";
export const OFFLINE_PAYMENT_CREATED = "OFFLINE_PAYMENT_CREATED";
export const PAYMENT_INTENT_CREATED = "PAYMENT_INTENT_CREATED";
export const PAYMENT_INTENT_UPDATED = "PAYMENT_INTENT_UPDATED";
export const PAYMENT_CONFIRMED = "PAYMENT_CONFIRMED";
export const CART_PAYMENT_PRECONDITION_FAILED =
  "CART_PAYMENT_PRECONDITION_FAILED";
export const REQUEST_PAYMENT_LOCK = "REQUEST_PAYMENT_LOCK";
export const PAYMENT_LOCK_ACQUIRED = "PAYMENT_LOCK_ACQUIRED";
export const PAYMENT_LOCK_RELEASED = "PAYMENT_LOCK_RELEASED";

export const customErrorHandler =
  (callback, err, res) => (dispatch, getState) => {
    const code = err.status;

    dispatch(stopLoading());

    switch (code) {
      case ERROR_CODE_404:
        callback()(dispatch, getState);
        break;
      default:
        dispatch(snackbarErrorHandler(err, res));
    }
  };

// Centralized handler for HTTP 412 Precondition Failed responses from checkout
// endpoints. A 412 means the cart is no longer in a payable state (the cleanup
// job reverted CheckedOut/PendingPayment back to Open after the TTL). On 412
// we dispatch CART_PAYMENT_PRECONDITION_FAILED (which the reducer maps to
// clearing paymentIntent/paymentProfile and setting the redirect flag),
// surface an info snackbar, and fire-and-forget refetch the cart so Redux
// matches server state by the time StripeView's <Navigate> lands. On any
// other status we delegate to the fallback handler the call site already
// owns (e.g. snackbarErrorHandler, or customErrorHandler(PaymentProfileNotFound)
// for getPaymentProfile's 404 path).
//
// The refetch is intentionally NOT awaited — StripeView must redirect
// immediately based on the flag; the fresh cart races behind the navigation.
export const cartStaleErrorHandler =
  (fallback) => (err, res) => (dispatch, getState) => {
    if (err.status === ERROR_CODE_412) {
      dispatch(stopLoading());
      dispatch(createAction(CART_PAYMENT_PRECONDITION_FAILED)({}));
      setSnackbarMessage({
        title: i18n.t("errors.cart_session_expired_title"),
        html: i18n.t("errors.cart_session_expired"),
        type: "info"
      })(dispatch);
      getSponsorCurrentCart()(dispatch, getState);
      return;
    }
    fallback(err, res)(dispatch, getState);
  };

const createCartCallback = () => (dispatch, getState) => {
  createNewCart()(dispatch, getState);
};

export const createNewCart = () => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand:
      "forms.add_on,notes,forms.items,forms.items.type,forms.items.meta_fields,forms.items.meta_fields.values,forms.items.images,fees"
  };

  return (
    postRequest(
      null,
      createAction(RECEIVE_CURRENT_CART),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current`,
      {},
      snackbarErrorHandler
    )(params)(dispatch)
      // Swallow the request util's trailing rejection (error UX already ran in
      // snackbarErrorHandler). Without this, fire-and-forget callers surface it
      // as an unhandled promise rejection.
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      })
  );
};

export const getSponsorCurrentCart = () => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand:
      "forms.add_on,notes,forms.items,forms.items.type,forms.items.meta_fields,forms.items.meta_fields.values,forms.items.images,fees"
  };

  return (
    getRequest(
      createAction(REQUEST_CURRENT_CART),
      createAction(RECEIVE_CURRENT_CART),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current`,
      (err, res) => (dispatch) =>
        customErrorHandler(createCartCallback, err, res)(dispatch, getState)
    )(params)(dispatch)
      // Swallow the request util's trailing rejection (error UX already ran in
      // customErrorHandler). Without this, fire-and-forget callers — including
      // cartStaleErrorHandler's 412 refetch — surface it as an unhandled
      // promise rejection.
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      })
  );
};

export const addSponsorFormToCart =
  (formId, addonId, items) => async (dispatch, getState) => {
    const { showAccessState } = getState();
    const { showAccess } = showAccessState;
    const accessToken = await getAccessTokenSafely();

    dispatch(startLoading());

    const params = {
      access_token: accessToken
    };

    const payload = {
      form_id: formId,
      items: normalizeItems(items)
    };

    if (addonId !== "none") payload.addon_id = addonId;

    return postRequest(
      null,
      createAction(FORM_ADDED_TO_CART),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current/forms`,
      payload,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        getSponsorCurrentCart()(dispatch, getState);
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  };

export const getCartForm = (formId) => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand:
      "items,items.meta_fields,items.meta_fields.values,items.type,items.images"
  };

  return (
    getRequest(
      createAction(REQUEST_CART_FORM),
      createAction(RECEIVE_CART_FORM),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current/forms/${formId}`,
      snackbarErrorHandler
    )(params)(dispatch)
      // Swallow the request util's trailing rejection (error UX already ran in
      // snackbarErrorHandler). Without this, fire-and-forget callers surface it
      // as an unhandled promise rejection.
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      })
  );
};

export const updateCartForm = (formId, items) => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  const payload = {
    form_id: formId,
    items: normalizeItems(items)
  };

  return putRequest(
    null,
    createAction(CART_FORM_UPDATED),
    `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current/forms/${formId}`,
    payload,
    snackbarErrorHandler
  )(params)(dispatch)
    .then(() => {
      dispatch(
        snackbarSuccessHandler({
          title: i18n.t("general.success"),
          html: i18n.t("my_cart.update_form.success")
        })
      );
    })
    .finally(() => {
      dispatch(stopLoading());
    });
};

const normalizeItems = (items) =>
  items.map((item) => ({
    ...item,
    meta_fields: item.meta_fields.filter((item) => item.current_value !== null)
  }));

export const deleteCartForm = (formId) => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  return (
    deleteRequest(
      null,
      createAction(CART_FORM_DELETED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current/forms/${formId}`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        getSponsorCurrentCart()(dispatch, getState);
        dispatch(
          snackbarSuccessHandler({
            title: i18n.t("general.success"),
            html: i18n.t("my_cart.delete_form.success")
          })
        );
      })
      // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      })
  );
};

export const checkoutCart = () => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken,
    expand:
      "forms.add_on,notes,forms.items,forms.items.type,forms.items.meta_fields,forms.items.meta_fields.values,forms.items.images,fees"
  };

  return (
    putRequest(
      null,
      createAction(CART_STATUS_UPDATED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current/checkout`,
      {},
      snackbarErrorHandler
    )(params)(dispatch)
      // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      })
  );
};

export const reOpenCart = () => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  return (
    deleteRequest(
      null,
      createAction("DUMMY_ACTION"),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current/checkout`,
      null,
      snackbarErrorHandler
    )(params)(dispatch)
      .then(() => {
        // Clear the payment-in-progress flag only after the DELETE succeeds so
        // the expiry redirect guard is not bypassed if the reopen request fails.
        dispatch(createAction(PAYMENT_LOCK_RELEASED)({}));
        getSponsorCurrentCart()(dispatch, getState);
      })
      // fire-and-forget dispatch; error UX already ran in snackbarErrorHandler
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      })
  );
};

// Acquires the cart payment lock by transitioning the cart
// PendingPayment → InitiatedPayment via PUT .../carts/current/init-payment.
// Called from StripeForm.handleSubmit BEFORE stripe.confirmPayment so the
// reopen-old-carts cron cannot reopen the cart mid-confirmation. The endpoint
// is idempotent (re-acquiring an already-locked cart returns 200).
//
// ⛔ Unlike reOpenCart, this MUST NOT chain getSponsorCurrentCart() and MUST
// use a no-op action: replacing redux currentCart with the InitiatedPayment
// cart would re-trigger StripeView's useEffect([currentCart]) →
// getPaymentProfile → createPaymentIntent against an InitiatedPayment cart
// (backend rejects with 412), aborting the in-flight payment.
//
// Errors route through cartStaleErrorHandler like the other payable-cart
// endpoints: a 412 (cart no longer PendingPayment) drives the
// precondition-failed redirect; any other status falls back to the snackbar.
// getState is forwarded to the curried request chain because the 412 path
// invokes getSponsorCurrentCart, which needs it. The returned promise rejects
// on error so the caller (handleSubmit) can abort before confirming payment.
export const lockCartForPayment = () => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  return putRequest(
    // flags the payment as soon as the lock is requested, so Pay with invoice is disabled meanwhile
    createAction(REQUEST_PAYMENT_LOCK),
    createAction(PAYMENT_LOCK_ACQUIRED),
    `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current/init-payment`,
    {},
    cartStaleErrorHandler(snackbarErrorHandler)
  )(params)(dispatch, getState).catch((err) => {
    // a stuck flag would also block the expiry redirect; rethrow so handleSubmit aborts
    dispatch(createAction(PAYMENT_LOCK_RELEASED)({}));
    throw err;
  });
};

export const unlockCartForPayment = () => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  return deleteRequest(
    null,
    createAction(PAYMENT_LOCK_RELEASED),
    `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/carts/current/init-payment`,
    {},
    cartStaleErrorHandler(snackbarErrorHandler)
  )(params)(dispatch, getState);
};

const createPaymentIntent = () => async (dispatch, getState) => {
  const { showAccessState, checkoutState } = getState();
  const { showAccess } = showAccessState;
  const { currentCart } = checkoutState;
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  const payload = {
    type: "Online",
    cart_id: currentCart.id
  };

  return postRequest(
    null,
    createAction(PAYMENT_INTENT_CREATED),
    `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/payments`,
    payload,
    cartStaleErrorHandler(snackbarErrorHandler)
  )(params)(dispatch, getState);
};

const PaymentProfileNotFound = () => (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const summitName = showAccess.summit.name;

  setSnackbarMessage({
    title: i18n.t("errors.payment_profile_not_found_title"),
    html: i18n.t("errors.payment_profile_not_found", { summitName }),
    type: "error"
  })(dispatch, getState);
};

export const getPaymentProfile = () => async (dispatch, getState) => {
  const { showAccessState } = getState();
  const { showAccess } = showAccessState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  return (
    getRequest(
      null,
      createAction(RECEIVE_PAYMENT_PROFILE),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/payment-profiles/SponsorServices`,
      cartStaleErrorHandler(
        (err, res) => (dispatch) =>
          customErrorHandler(
            PaymentProfileNotFound,
            err,
            res
          )(dispatch, getState)
      )
    )(params)(dispatch, getState)
      .then(() => createPaymentIntent()(dispatch, getState))
      // No-op .catch tail: after cartStaleErrorHandler runs inside the request
      // util on a 412 from getRequest OR from createPaymentIntent's chained
      // postRequest, the outer promise still rejects. Swallow it here so the
      // already-dispatched precondition flow is not shadowed by an unhandled-
      // rejection warning. Do NOT log it — that hides real bugs.
      .catch(() => {})
      .finally(() => {
        dispatch(stopLoading());
      })
  );
};

export const payWithInvoice = () => async (dispatch, getState) => {
  const { showAccessState, checkoutState } = getState();
  const { showAccess } = showAccessState;
  const { currentCart } = checkoutState;
  const accessToken = await getAccessTokenSafely();

  dispatch(startLoading());

  const params = {
    access_token: accessToken
  };

  const payload = {
    type: "Offline",
    cart_id: currentCart.id
  };

  return (
    postRequest(
      null,
      createAction(OFFLINE_PAYMENT_CREATED),
      `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/payments`,
      payload,
      snackbarErrorHandler
    )(params)(dispatch)
      // rejects on error so callers only navigate on success; they own the .catch
      .then(() => {
        getSponsorCurrentCart()(dispatch, getState);
      })
  );
};

export const updatePaymentIntent =
  (paymentMethod) => async (dispatch, getState) => {
    const { showAccessState, checkoutState } = getState();
    const { showAccess } = showAccessState;
    const { currentCart, paymentIntent } = checkoutState;
    const accessToken = await getAccessTokenSafely();

    const params = {
      access_token: accessToken
    };

    const payload = {
      payment_method: paymentMethod,
      cart_id: currentCart.id
    };

    return (
      putRequest(
        null,
        createAction(PAYMENT_INTENT_UPDATED),
        `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/payments/${paymentIntent.id}/reprice`,
        payload,
        cartStaleErrorHandler(snackbarErrorHandler)
      )(params)(dispatch, getState)
        // dispatched fire-and-forget from the Stripe PaymentElement's "change" event; swallow so a reprice failure doesn't surface as an unhandled rejection
        .catch(() => {})
    );
  };

export const confirmPayment = (status) => async (dispatch, getState) => {
  const { showAccessState, checkoutState } = getState();
  const { showAccess } = showAccessState;
  const { paymentIntent } = checkoutState;
  const accessToken = await getAccessTokenSafely();

  const params = {
    access_token: accessToken
  };

  // Async methods such as ACH bank debit settle later and report "processing"
  // at confirm time; cards capture synchronously and report "succeeded".
  const isProcessing = status === "processing";

  return putRequest(
    null,
    createAction(PAYMENT_CONFIRMED),
    `${window.PURCHASES_API_URL}/api/v1/summits/${showAccess.summit.id}/sponsors/${showAccess.sponsor.id}/payments/${paymentIntent.id}/confirm`,
    {},
    cartStaleErrorHandler(snackbarErrorHandler)
  )(params)(dispatch, getState).then(() => {
    getSponsorCurrentCart()(dispatch, getState);
    dispatch(
      snackbarSuccessHandler({
        title: i18n.t(
          isProcessing
            ? "my_cart_checkout.payment.processing_title"
            : "general.success"
        ),
        html: i18n.t(
          isProcessing
            ? "my_cart_checkout.payment.processing"
            : "my_cart_checkout.payment.success"
        )
      })
    );
  });
};
